import { useState } from "react";
import { Box, Container, Typography, useMediaQuery, useTheme } from "@mui/material";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const GOLD = "#D97706";

const FEATURES = [
  {
    id: "kyc", Icon: BadgeOutlinedIcon, tag: "Onboarding", accent: "#2563EB",
    title: "KYC Entity & Individual Management",
    body: "Onboard legal entities and natural persons with structured KYC forms, document uploads, shareholder trees, and UBO identification. Every field tracked, every change logged.",
    highlights: ["Guided digital onboarding forms", "Document upload & verification", "Shareholder tree & UBO mapping"],
    stat: { value: "100%", label: "Digital onboarding — no paperwork" },
  },
  {
    id: "pep", Icon: ManageSearchIcon, tag: "Screening", accent: GOLD,
    title: "PEP & Sanctions Screening",
    body: "Instant hits across 200+ global lists — OFAC, UN, EU, HMT, UAE Local Terrorist List — with configurable match thresholds that reduce false positives without missing real risks.",
    highlights: ["200+ global sanctions lists", "Configurable match thresholds", "Real-time & batch screening"],
    stat: { value: "200+", label: "Sanctions & PEP lists monitored" },
  },
  {
    id: "risk", Icon: AssessmentOutlinedIcon, tag: "Risk", accent: "#7C3AED",
    title: "Customer Risk Profiling",
    body: "A composite risk score is calculated across nationality, business type, delivery channel, funds origin, and residency status — giving your team a clear, defensible risk picture.",
    highlights: ["Multi-factor risk scoring", "Regulator-aligned risk matrices", "Automatic high-risk flagging"],
    stat: { value: "5+", label: "Risk dimensions scored per customer" },
  },
  {
    id: "audit", Icon: FactCheckOutlinedIcon, tag: "Compliance", accent: "#16A34A",
    title: "Immutable Audit Trail",
    body: "Every action logged — who did what, when, and why. Examination-ready audit records are maintained automatically so your team is always prepared for a regulatory visit.",
    highlights: ["Tamper-proof activity logs", "User-level action tracking", "Regulator examination ready"],
    stat: { value: "100%", label: "Actions captured automatically" },
  },
  {
    id: "report", Icon: SummarizeOutlinedIcon, tag: "Reporting", accent: "#EA580C",
    title: "FIU-Ready Regulatory Reports",
    body: "Generate DPMSR, STR, and CTR reports as regulator-approved XML templates and PDFs. Submit directly to the UAE FIU in seconds, not hours of manual preparation.",
    highlights: ["DPMSR / STR / CTR generation", "Regulator-approved XML & PDF", "One-click FIU submission"],
    stat: { value: "3", label: "Report types: DPMSR, STR, CTR" },
  },
  {
    id: "alerts", Icon: NotificationsActiveOutlinedIcon, tag: "Monitoring", accent: "#DC2626",
    title: "Automated Alerts & Re-screening",
    body: "Screening hits, expiring KYC documents, re-screening schedules, and renewal reminders are automated and surfaced in your compliance dashboard — nothing falls through the cracks.",
    highlights: ["Expiry & renewal reminders", "Scheduled re-screening", "Dashboard alert centre"],
    stat: { value: "0", label: "Manual follow-ups needed" },
  },
  {
    id: "country", Icon: TravelExploreOutlinedIcon, tag: "Intelligence", accent: "#0891B2",
    title: "Country Risk Intelligence",
    body: "Configurable country risk matrices aligned to FATF grey and black lists, allowing your team to apply your own risk appetite while staying regulator-compliant.",
    highlights: ["FATF grey & black list sync", "Configurable per-country scores", "Automatic matrix updates"],
    stat: { value: "195+", label: "Countries in the risk matrix" },
  },
  {
    id: "ubo", Icon: AccountTreeOutlinedIcon, tag: "Ownership", accent: "#059669",
    title: "UBO & Shareholder Mapping",
    body: "Visualise complete corporate ownership chains down to the ultimate beneficial owner. PEP and sanctions links are flagged at every ownership tier automatically.",
    highlights: ["Multi-tier ownership trees", "PEP links flagged at every level", "Visual ownership charts"],
    stat: { value: "∞", label: "Ownership tiers supported" },
  },
];

/* ── Detail panel — shared between desktop and mobile ── */
function FeatureDetail({ feat }) {
  return (
    <>
      {/* Top */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2.5, mb: 3 }}>
          <Box sx={{
            width: 56, height: 56, borderRadius: "14px", flexShrink: 0,
            background: `${feat.accent}12`, border: `1px solid ${feat.accent}22`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <feat.Icon sx={{ fontSize: 28, color: feat.accent }} />
          </Box>
          <Box>
            <Box sx={{
              display: "inline-block", px: 1.5, py: .4, borderRadius: "6px",
              background: `${feat.accent}10`, border: `1px solid ${feat.accent}20`,
              fontSize: 10, fontWeight: 700, color: feat.accent,
              letterSpacing: "1.5px", textTransform: "uppercase",
              fontFamily: "IBM Plex Sans, sans-serif", mb: .75,
            }}>
              {feat.tag}
            </Box>
            <Typography sx={{ fontSize: { xs: 17, md: 24 }, fontWeight: 700, color: "#0F172A", lineHeight: 1.25, fontFamily: "IBM Plex Sans, sans-serif" }}>
              {feat.title}
            </Typography>
          </Box>
        </Box>
        <Typography sx={{ fontSize: 15, color: "#64748B", lineHeight: 1.85, fontFamily: "IBM Plex Sans, sans-serif" }}>
          {feat.body}
        </Typography>
      </Box>

      {/* Highlights */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 1.5, mb: 3 }}>
        {feat.highlights.map(h => (
          <Box key={h} sx={{
            display: "flex", alignItems: "flex-start", gap: 1.25,
            p: 2, borderRadius: "10px",
            background: "#F8FAFC", border: "1px solid #F1F5F9",
          }}>
            <CheckCircleIcon sx={{ fontSize: 14, color: feat.accent, mt: ".15em", flexShrink: 0 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#475569", lineHeight: 1.45, fontFamily: "IBM Plex Sans, sans-serif" }}>
              {h}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Stat bar */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: 2,
        p: 2.5, borderRadius: "10px",
        background: `${feat.accent}08`, border: `1px solid ${feat.accent}18`,
      }}>
        <Typography sx={{ fontSize: 26, fontWeight: 700, color: feat.accent, lineHeight: 1, fontFamily: "IBM Plex Sans, sans-serif" }}>
          {feat.stat.value}
        </Typography>
        <Typography sx={{ fontSize: 14, color: "#64748B", fontFamily: "IBM Plex Sans, sans-serif" }}>
          {feat.stat.label}
        </Typography>
      </Box>
    </>
  );
}

export default function SolutionsSection() {
  const theme      = useTheme();
  const isMobile   = useMediaQuery(theme.breakpoints.down("lg"));

  /* Desktop: one active tab  |  Mobile: one expanded accordion (null = collapsed) */
  const [active,   setActive]   = useState(0);
  const [expanded, setExpanded] = useState(0);

  const toggleExpand = (i) => setExpanded(v => v === i ? null : i);

  return (
    <Box
      id="features"
      sx={{ background: "#F8FAFC", py: { xs: 10, md: 16 }, borderTop: "1px solid #E2E8F0" }}
    >
      <Container maxWidth="xl">

        {/* Header */}
        <Box sx={{ textAlign: "center", mb: { xs: 7, md: 9 } }}>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: GOLD, letterSpacing: "3px", textTransform: "uppercase", mb: 2, fontFamily: "IBM Plex Sans, sans-serif" }}>
            Platform Capabilities
          </Typography>
          <Typography component="h2" className="font-display" sx={{ fontSize: { xs: 28, md: 46 }, fontWeight: 700, color: "#0F172A", letterSpacing: "-1.5px", lineHeight: 1.1, mb: 2 }}>
            Everything AML. One platform.
          </Typography>
          <Typography sx={{ fontSize: 17, color: "#64748B", maxWidth: 520, mx: "auto", lineHeight: 1.8, fontFamily: "IBM Plex Sans, sans-serif" }}>
            From onboarding to ongoing monitoring to regulatory reporting — AMLDiligence covers the full AML-CFT lifecycle.
          </Typography>
        </Box>

        {/* ── Mobile accordion ── */}
        {isMobile && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {FEATURES.map((f, i) => {
              const open = expanded === i;
              return (
                <Box
                  key={f.id}
                  sx={{
                    background: "#fff",
                    border: "1px solid",
                    borderColor: open ? f.accent : "#E2E8F0",
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: open ? `0 8px 32px ${f.accent}14` : "0 1px 4px rgba(15,23,42,.04)",
                    transition: "border-color .25s, box-shadow .3s",
                  }}
                >
                  {/* Accordion header */}
                  <Box
                    onClick={() => toggleExpand(i)}
                    sx={{
                      display: "flex", alignItems: "center", gap: 2,
                      px: 2.5, py: 2, cursor: "pointer",
                      background: open ? `${f.accent}06` : "transparent",
                      transition: "background .2s",
                    }}
                  >
                    <Box sx={{
                      width: 40, height: 40, borderRadius: "10px", flexShrink: 0,
                      background: open ? `${f.accent}18` : `${f.accent}10`,
                      border: `1px solid ${f.accent}22`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      transition: "background .2s",
                    }}>
                      <f.Icon sx={{ fontSize: 20, color: f.accent }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{
                        fontSize: 14, fontWeight: 600,
                        color: open ? "#0F172A" : "#475569",
                        lineHeight: 1.3, fontFamily: "IBM Plex Sans, sans-serif",
                      }}>
                        {f.title}
                      </Typography>
                      <Box component="span" sx={{
                        fontSize: 10, fontWeight: 700, color: f.accent,
                        textTransform: "uppercase", letterSpacing: "1px",
                        fontFamily: "IBM Plex Sans, sans-serif",
                      }}>
                        {f.tag}
                      </Box>
                    </Box>
                    <ExpandMoreIcon sx={{
                      fontSize: 20, color: open ? f.accent : "#CBD5E1", flexShrink: 0,
                      transform: open ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform .3s cubic-bezier(.4,0,.2,1), color .2s",
                    }} />
                  </Box>

                  {/* Accordion body — animated with max-height */}
                  <Box sx={{
                    maxHeight: open ? "1000px" : "0px",
                    overflow: "hidden",
                    transition: open
                      ? "max-height .45s cubic-bezier(0,.5,.3,1)"
                      : "max-height .3s cubic-bezier(.4,0,1,1)",
                  }}>
                    <Box sx={{
                      px: 2.5, pb: 3, pt: 1,
                      opacity: open ? 1 : 0,
                      transition: "opacity .3s",
                    }}>
                      <FeatureDetail feat={f} />
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {/* ── Desktop tab explorer ── */}
        {!isMobile && (
          <>
            <Box sx={{ display: "flex", gap: 3, alignItems: "stretch" }}>

              {/* Left — feature list */}
              <Box sx={{ width: "34%", display: "flex", flexDirection: "column", gap: 1 }}>
                {FEATURES.map((f, i) => (
                  <Box
                    key={f.id}
                    onClick={() => setActive(i)}
                    sx={{
                      display: "flex", alignItems: "center", gap: 2,
                      px: 2.5, py: 1.75,
                      borderRadius: "12px",
                      cursor: "pointer",
                      border: "1px solid",
                      borderColor: active === i ? f.accent : "#E2E8F0",
                      background: active === i ? `${f.accent}08` : "#fff",
                      boxShadow: active === i ? `0 4px 16px ${f.accent}14` : "0 1px 3px rgba(15,23,42,.04)",
                      transition: "all .2s cubic-bezier(.4,0,.2,1)",
                      "&:hover": {
                        borderColor: f.accent,
                        background: `${f.accent}06`,
                        transform: "translateX(2px)",
                      },
                    }}
                  >
                    <Box sx={{
                      width: 38, height: 38, borderRadius: "10px", flexShrink: 0,
                      background: active === i ? `${f.accent}18` : `${f.accent}10`,
                      border: `1px solid ${f.accent}22`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <f.Icon sx={{ fontSize: 19, color: f.accent }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{
                        fontSize: 13, fontWeight: 600,
                        color: active === i ? "#0F172A" : "#475569",
                        lineHeight: 1.3, fontFamily: "IBM Plex Sans, sans-serif",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      }}>
                        {f.title}
                      </Typography>
                      <Typography sx={{
                        fontSize: 10, fontWeight: 600, color: f.accent,
                        textTransform: "uppercase", letterSpacing: "1px",
                        fontFamily: "IBM Plex Sans, sans-serif",
                      }}>
                        {f.tag}
                      </Typography>
                    </Box>
                    <ArrowForwardIosIcon sx={{
                      fontSize: 12, flexShrink: 0,
                      color: active === i ? FEATURES[active].accent : "#CBD5E1",
                      transition: "color .2s",
                    }} />
                  </Box>
                ))}
              </Box>

              {/* Right — feature detail */}
              <Box sx={{ flex: 1 }}>
                <Box sx={{
                  background: "#fff",
                  border: `1px solid ${FEATURES[active].accent}25`,
                  borderRadius: "20px",
                  p: 5,
                  height: "100%",
                  boxShadow: `0 8px 48px ${FEATURES[active].accent}0e`,
                  transition: "border-color .25s, box-shadow .25s",
                  display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 4,
                }}>
                  <FeatureDetail feat={FEATURES[active]} />
                </Box>
              </Box>
            </Box>

            {/* Dot pagination */}
            <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 4, flexWrap: "wrap" }}>
              {FEATURES.map((f, i) => (
                <Box
                  key={f.id}
                  onClick={() => setActive(i)}
                  sx={{
                    width: 8, height: 8, borderRadius: "50%", cursor: "pointer",
                    background: active === i ? f.accent : "#CBD5E1",
                    transition: "all .2s",
                    transform: active === i ? "scale(1.4)" : "scale(1)",
                  }}
                />
              ))}
            </Box>
          </>
        )}
      </Container>
    </Box>
  );
}
